<template>
  <div class="container full-height">
    <preview-page-header
      class="slds-m-bottom--small"
      :preview="preview"
      :loading="loading"
      :mode="mode"
      @cancel="cancel"
      @openEditMode="openEditMode"
      @save="save"
      @update="update" />

    <!-- Wizard -->
    <card additional-classes="slds-m-bottom--small" v-if="currentWizard && Object.keys(currentWizard).length > 1">
      <div class="slds-path slds-m-top--small">
        <div class="row">
          <button
            class="slds-button slds-button_neutral"
            @click="goToPreviousStep"
            :disabled="isBackButtonDisabled"
          >{{ $t('action.back') }}</button>
          <button
            class="slds-button slds-button_neutral"
            @click="goToNextStep"
            :disabled="isNextButtonDisabled"
          >{{ $t('action.next') }}</button>
        </div>
        <div class="slds-grid slds-path__track slds-m-top--small">
          <div class="slds-grid slds-path__scroller-container">
            <div class="slds-path__scroller" role="application">
              <div class="slds-path__scroller_inner">
                <ul class="slds-path__nav" role="listbox" aria-orientation="horizontal">
                  <li
                    class="slds-path__item"
                    :class="getClassByStatus(tempStep)"
                    role="presentation"
                    v-for="(tempStep, index) in Object.keys(currentWizard)"
                    :key="currentWizard[tempStep].stepNumber"
                  >
                    <a
                      :aria-selected="localStep === localStep.stepNumber"
                      :id="'path-' + index"
                      class="slds-path__link"
                      role="option"
                      tabindex="-1"
                    >
                      <span class="slds-path__stage">
                        <svg class="slds-icon slds-icon_x-small" aria-hidden="true">
                          <use xlink:href="../assets/symbols.svg#check" />
                        </svg>
                        <span class="slds-assistive-text">{{$t('wizard.step.completed')}}</span>
                      </span>
                      <span class="slds-path__title">{{ $t(currentWizard[tempStep].label) }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </card>

    <!-- Step -->
    <transition :name="currentTransition" mode="out-in">
      <keep-alive>
        <component
          v-if="currentWizard && localStep && currentWizard[localStep]"
          class="content"
          ref="step"
          :config="config"
          :is="currentWizard[localStep].component"
          :loading="loading"
          :mode="mode"
          @callFunction="handleCallFunction"
          @startLoading="loading = true"
          @endLoading="loading = false" />
      </keep-alive>
    </transition>
  </div>
</template>
<script>
import Card from '../components/slds/Card'
import CheckPermission from '../components/utils/CheckPermission'
import HasMode from '../components/utils/HasMode'
import PreviewPageHeader from '../components/fast-counter/PreviewPageHeader'
import { fastCounter } from '../config/wizards'
import { getErrorMessage } from '../components/utils/utils'
import { mapActions } from 'vuex'

export default {
  mixins: [CheckPermission, HasMode],
  components: {
    Card,
    PreviewPageHeader
  },
  props: {
    previewId: {
      type: String
    },
    step: {
      type: String,
      default: 'previewData'
    }
  },
  data () {
    return {
      config: {},
      currentTransition: 'slide-fade-right',
      currentWizard: {},
      loading: false,
      mode: '',
      localStep: '',
      preview: {}
    }
  },
  methods: {
    ...mapActions({
      getEmptyConfig: 'fastCounter/getEmptyConfig',
      savePreview: 'fastCounter/savePreview',
    }),
    cancel () {
      this.$store.dispatch('fastCounter/loadOldState')
      this.mode = 'view'
      this.oldState = ''
      this.$refs.step.callLoadFromStore()
    },
    getClassByStatus (step) {
      if (this.localStep) {
        const currentStepNumber = this.currentWizard[this.localStep].stepNumber
        const targetStepNumber = this.currentWizard[step].stepNumber
        if (currentStepNumber < targetStepNumber) {
          return 'slds-is-incomplete'
        } else if (currentStepNumber === targetStepNumber) {
          return 'slds-is-active'
        } else {
          return 'slds-is-complete'
        }
      } else {
        return 'slds-is-incomplete'
      }
    },
    async goToNextStep () {
      if (this.$refs.step.validate) {
        const isValidStep = this.$refs.step.validate()
        if (isValidStep !== undefined) {
          if (isValidStep) {
            if (this.isCreate || this.isEdit) {
              const object = this.$refs.step.getSectionToSave()
              await this.$store.dispatch('fastCounter/updatePreviewSection', { data: object, section: this.localStep, vm: this })
            }
            const currentStepNumber = this.currentWizard[this.localStep].stepNumber
            if (currentStepNumber + 1 !== Object.keys(this.currentWizard).length) {
              this.currentTransition = 'slide-fade-left'
              this.localStep = Object.keys(this.currentWizard).find(key => this.currentWizard[key].stepNumber === currentStepNumber + 1)
              if (!this.isCreate) {
                this.$router.push(`/preview/${this.preview.id}/${this.localStep}`)
              }
            }
          } else {
            this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('validation.invalidDataMessage'))
          }
        }
      }
    },
    async goToPreviousStep () {
      if ((this.isCreate || this.isEdit) && this.$refs.step.getSectionToSave) {
        await this.$store.dispatch('fastCounter/updatePreviewSection', { data: this.$refs.step.getSectionToSave(), section: this.localStep, vm: this })
      }
      const currentStepNumber = this.currentWizard[this.localStep].stepNumber
      if (currentStepNumber !== 0) {
        this.currentTransition = 'slide-fade-right'
        this.localStep = Object.keys(this.currentWizard).find(key => this.currentWizard[key].stepNumber === currentStepNumber - 1)
        if (!this.isCreate) {
          this.$router.push(`/preview/${this.preview.id}/${this.localStep}`)
        }
      }
    },
    openEditMode () {
      this.oldState = JSON.stringify(this.$store.getters['fastCounter/currentPreview'])
      this.$store.dispatch('fastCounter/saveOldState')
      this.mode = 'edit'
    },
    async save () {
      try {
        if (this.$refs.step.validate) {
          const isValidStep = this.$refs.step.validate()
          if (isValidStep !== undefined) {
            if (isValidStep) {
              this.loading = true
              if (this.$refs.step.getSectionToSave) {
                const object = this.$refs.step.getSectionToSave()
                await this.$store.dispatch('fastCounter/updatePreviewSection', { data: object, section: this.localStep, vm: this })
              }
              const record = await this.savePreview()
              this.showToast('success', this.$t('general.success'), 'text', this.$t('message.create'))
              this.preview = record
              this.mode = 'view'
              this.$router.push(`/preview/${record.id}/${this.localStep}`)
              this.loading = false
              return record.id
            } else {
              console.error(error)
              this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('validation.invalidDataMessage'))
            }
          }
        }
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    },
    async update () {
      try {
        if (this.$refs.step.validate) {
          const isValidStep = this.$refs.step.validate()
          if (isValidStep !== undefined) {
            if (isValidStep) {
              this.loading = true
              const object = this.$refs.step.getSectionToSave ? this.$refs.step.getSectionToSave() : {}
              await this.$store.dispatch('fastCounter/updatePreviewSection', { data: object, section: this.localStep, vm: this })
              const record = (await this.$store.dispatch('fastCounter/update', { previewId: this.preview.id }))
              this.showToast('success', this.$t('general.success'), 'text', this.$t('message.update'))
              this.mode = 'view'
              this.loading = false
              this.preview = record
              return this.preview.id
            } else {
              console.error(error)
              this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('validation.invalidDataMessage'))
            }
          }
        }
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    },
    async handleCallFunction (event) {
      const { functionToCall, callback } = event
      try {
        const response = await this[functionToCall]()
        await callback(response)
      } catch (error) {
        throw error
      }
    },
  },
  async created () {
    try {
      let mode = ''
      this.currentWizard = fastCounter
      if (this.previewId) {
        mode = 'view'
        const preview = await this.$store.dispatch('fastCounter/getPreview', this.previewId)
        if (preview.httpCode && preview.httpCode === '404') {
          this.$router.push('/preview-not-found')
          return
        } else {
          this.preview = preview
          this.config = _.cloneDeep(preview.config)
        }
      } else if (this.$route.fullPath.includes('/new')) {
        mode = 'create'
        const config = (await this.getEmptyConfig()).FASTCOUNTER
        await this.$store.dispatch('fastCounter/loadConfig', config)
        this.config = config
      }
      this.mode = mode
      if (this.step) {
        if (Object.keys(this.currentWizard).includes(this.step)) {
          this.localStep = this.step
        } else {
          this.$router.push(`/sectionNotFound/${this.campaignId}`)
          return
        }
      } else {
        this.localStep = Object.keys(this.currentWizard)[0]
      }
      if (!this.isCreate && !this.$router.currentRoute.path.includes(this.localStep)) {
        this.$router.push(`/preview/${this.previewId}/${this.localStep}`)
      }
    } catch (error) {
      console.error(error)
      this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
    }
  },
  computed: {
    isBackButtonDisabled () {
      return (this.localStep && this.currentWizard[this.localStep].stepNumber === 0) || this.loading
    },
    isNextButtonDisabled () {
      return (this.localStep && this.currentWizard[this.localStep].stepNumber === Object.keys(this.currentWizard).length - 1) || this.loading
    }
  }
}
</script>
<style lang="scss" scoped>
  .container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  .full-height {
    height: 100%;
  }
  .row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
}

</style>
